import { Pic } from "./Pic";

import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskLines } from "./Motion";
import { HeroSearch } from "./HeroSearch";
import { scrollToId } from "@/lib/scroll";
import { useT } from "@/i18n";

const LAMELLAE = 8;

const Curtain = () => (
  <div className="pointer-events-none absolute inset-0 z-20 flex flex-col">
    {Array.from({ length: LAMELLAE }).map((_, i) => (
      <motion.div
        key={i}
        className={`flex-1 origin-right ${i % 2 ? "bg-[#C26D38]" : "bg-[#1E1C1A]"}`}
        initial={{ scaleX: 1 }}
        animate={{ scaleX: 0 }}
        transition={{ duration: 1.1, delay: 0.25 + i * 0.07, ease: [0.76, 0, 0.24, 1] }}
      />
    ))}
  </div>
);

const TiltFrame = () => {
  const t = useT();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} className="relative h-full w-full [perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative h-full w-full [transform-style:preserve-3d]" data-testid="hero-tilt-frame">
        <div className="hero-clip absolute inset-0 overflow-hidden bg-[#1E1C1A]">
          <Pic as={motion.img}
            src="/img/emma/hero-cupola-chiesa.jpg"
            alt={t("hero.alt", "Cupola di una chiesa con travi curve in legno lamellare Emma Industrie")}
            style={{ y: imgY }}
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-x-0 bottom-0 h-[115%] w-full object-cover object-[50%_35%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121110]/70 via-transparent to-transparent" />
          <Curtain />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.9 }}
          style={{ transform: "translateZ(60px)" }}
          className="absolute -left-4 top-8 z-30 rounded-sm border border-white/15 bg-[#121110]/80 px-5 py-4 backdrop-blur-xl sm:-left-10"
          data-testid="hero-spec-tag"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D98A52]">{t("hero.specTitle", "Legno lamellare · CE")}</p>
          <p className="mt-1 font-mono text-xs text-[#F7F5F0]">{t("hero.specText", "Travi fino a 44 m di lunghezza")}</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const t = useT();
  return (
  <section id="top" className="grain relative min-h-[100svh] overflow-hidden bg-[#121110] text-[#F7F5F0]" data-testid="hero-section">
    <div className="mx-auto grid min-h-[100svh] max-w-[1500px] grid-cols-1 gap-10 px-5 pb-10 pt-40 sm:px-8 lg:grid-cols-12 lg:pt-44">
      <div className="relative z-10 flex flex-col justify-between lg:col-span-7">
        <div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-[#A39E93]">
            {t("hero.kicker", "Legno lamellare — Strutture — Case in legno")}
          </motion.p>
          <MaskLines
            as="h1"
            onLoad
            baseDelay={4}
            className="font-display text-[13vw] font-extrabold uppercase leading-[0.86] tracking-[-0.03em] sm:text-7xl lg:text-[4.4rem] xl:text-[5.8rem]"
            lines={[
              <span key="a" style={{ fontStretch: "75%" }}>{t("hero.h1a", "Legno")}</span>,
              <span key="b" className="font-serif normal-case italic tracking-normal text-[#D98A52]">{t("hero.h1b", "lamellare")}</span>,
              <span key="c" className="whitespace-nowrap" style={{ fontStretch: "75%" }}>EMMA INDUSTRIE</span>,
            ]}
          />
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 1 }} className="mt-12 max-w-xl">
          <p className="text-base leading-relaxed text-[#A39E93] sm:text-lg">
            {t("hero.text", "Dal 1905, quattro generazioni di famiglia: legno lamellare, XLAM, carpenteria metallica e progettazione. Un unico interlocutore dal progetto alla realizzazione.")}
          </p>
          <HeroSearch />
          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={() => scrollToId("#contatti")} className="btn-amber" data-testid="hero-cta-preventivo">
              {t("hero.cta1", "Richiedi un preventivo")} <ArrowUpRight className="h-4 w-4" />
            </button>
            <button onClick={() => scrollToId("#prodotti")} className="btn-ghost" data-testid="hero-cta-prodotti">
              {t("hero.cta2", "Esplora i sistemi")} <ArrowDown className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      </div>
      <div className="relative h-[60vh] lg:col-span-5 lg:h-auto lg:min-h-[640px]">
        <TiltFrame />
      </div>
    </div>
    <div className="pointer-events-none absolute bottom-6 right-8 hidden items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-[#A39E93] lg:flex">
      <span className="scroll-line" /> Scroll
    </div>
  </section>
  );
};
